import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, resolveComponent as _resolveComponent, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "" }
const _hoisted_2 = {
  class: "mt-4",
  style: {"width":"100%","overflow":"auto"}
}

import moment from 'moment';
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useButtonCustom } from '@/core/hooks/use-button-custom';
import { useCreatingForm } from '@/core/hooks/use-creating-form';
import { useLoading } from '@/core/hooks/use-loading';
import { useDateTime } from '@/core/hooks/use-date-time-hook';
import { setCurrentPageTitle } from '@/core/helpers/breadcrumb';
import {
  ICapSuaChuaResponseBase,
  IChiTietXuatTheoDoiTuongResponseBase,
  IKhoResponseBase,
  IToaXeResponseBase
} from '@/core/interfaces/ApiResponses';
import { getErrorMsg, swalErrNotification, swalNotification } from '@/core/helpers/utils';
import { BaoCaoService } from '@/core/services/BaoCao.service';
import { KhoService } from '@/core/services/Kho.service';
import { CapSuaChuaService } from '@/core/services/CapSuaChuaService.service';
import ButtonCustom from '@/components/shared/buttons/ButtonCustom.vue';
import ChiTietXuatTheoDoiTuongTable from './table-components/ChiTietXuatTheoDoiTuongTable.vue';
import DivCol from '@/layout/_shared/DivCol.vue';
import DivRow from '@/layout/_shared/DivRow.vue';
import KeHoachChiPhiPrintModal from './print-modals/KeHoachChiPhiPrintModal.vue';
import LabelCustom from '@/components/shared/label-custom/LabelCustom.vue';
import PageRowLayout from '@/layout/_shared/PageRowLayout.vue';
import ResponsiveLayout from '@/layout/_shared/ResponsiveLayout.vue';
import ToaXeService from '@/core/services/ToaXe.service';


export default /*#__PURE__*/_defineComponent({
  __name: 'KeHoachChiPhi',
  setup(__props) {

const { disableDate, defaultDate } = useDateTime();

setCurrentPageTitle('Báo cáo');

const store = useStore();

const currentYear = computed(() => store.getters.getSelectedYear);

const { formRef } = useCreatingForm();

const { startLoading, endLoading } = useLoading();

const items = ref<IChiTietXuatTheoDoiTuongResponseBase[]>([]);

const { ButtonsType, ButtonTypeColors } = useButtonCustom();

const form = ref({
  from: defaultDate.value,
  to: defaultDate.value,
  khoId: -1,
  toaXeId: -1,
  capSuaChuaId: -1,
});

const isFiltersChanging = ref(true);

const khoItems = ref<IKhoResponseBase[]>([]);

const fromDateValidation = (_, value, callback) => {
  if (!value) {
    callback(new Error('Bắt buộc phải nhập'));
  }

  if (value && form.value.to && moment(value).isAfter(form.value.to)) {
    callback(new Error('Ngày bắt đầu phải trước ngày kết thúc'));
  }

  callback();
}

const toDateValidation = (_, value, callback) => {
  if (!value) {
    callback(new Error('Bắt buộc phải nhập'));
  }

  if (value && form.value.from && moment(value).isBefore(form.value.from)) {
    callback(new Error('Ngày bắt đầu phải trước ngày kết thúc'));
  }

  callback();
}

const rules = ref({
  from: [
    {
      validator: fromDateValidation,
      trigger: 'change',
    },
  ],
  to: [
    {
      validator: toDateValidation,
      trigger: 'change',
    },
  ],
  khoId: [
    {
      type: 'number',
      message: "Phải chọn kho",
      trigger: 'change',
      min : 1,
    },
  ],
  toaXeId: [
    {
      type: 'number',
      message: "Phải chọn toa xe",
      trigger: 'change',
      min : 1,
    },
  ],
  capSuaChuaId: [
    {
      type: 'number',
      message: "Phải cấp sửa chữa",
      trigger: 'change',
      min : 1,
    },
  ],
});

const fetchKhoItems = async () => {
  await startLoading();

  try {
    const { data: { data: { data } } } = await KhoService.fetchAllWarehouses();

    khoItems.value = data;

    await endLoading();
  } catch (e) {
    await endLoading();

    await swalErrNotification(e, 'Có lỗi khi lấy danh sách kho');
  }
}

const searchingToaXe = ref(false);

const toaXeOptions = ref<IToaXeResponseBase[]>([]);

const capSuaChuaItems = ref<ICapSuaChuaResponseBase[]>([]);

const fetchCapSuaChuaItems = async () => {
  try {
    startLoading();

    const { data: { data: { data } } } = await CapSuaChuaService.list({});

    capSuaChuaItems.value = data;

    endLoading();
  } catch (e) {
    endLoading();
    swalNotification(
      getErrorMsg(e, 'Có lỗi khi lấy thông tin cấp sửa chữa'),
      'error',
    );
  }
}

const searchToaXe = async (query) => {
  searchingToaXe.value = true;

  const { data: { data: { data: res } } } = await ToaXeService.list(
    1,
    2000,
    0,
    query,
  );

  toaXeOptions.value = res;

  searchingToaXe.value = false;
}

const selectingKho = computed(() => {
  if (!form.value.khoId) return null;

  return khoItems.value.find(({ id }) => id === form.value.khoId);
});

const selectingCapSuaChua = computed(() => {
  if (!form.value.capSuaChuaId) return null;

  return capSuaChuaItems.value.find(({ id }) => id === form.value.capSuaChuaId);
});

const selectingToaXe = computed(() => {
  if (!form.value.toaXeId) return null;

  return toaXeOptions.value.find(({ id }) => id === form.value.toaXeId);
});

watch(
  form,
  () => {
    isFiltersChanging.value = true;
  },
  {
    deep: true,
    immediate: true,
  }
);

onMounted(async () => {
  await fetchKhoItems();

  await fetchCapSuaChuaItems();
});

const showPrintButton = computed(() => {
  if (isFiltersChanging.value) return false;

  if (!items.value.length) return false;

  return !(!selectingKho.value ||
    !selectingToaXe.value ||
    !form.value.from ||
    !form.value.to ||
    !selectingCapSuaChua.value);
});

const printModalRef = ref();

const generate = () => {
  if (!formRef.value) return;

  formRef.value.validate(async (isValid: boolean) => {
    if (isValid) {
      try {
        await startLoading();

        const { data: { data: response } } = await BaoCaoService.getBangKeChiTietXuatTheoDoiTuong({
          capSuaChuaId: selectingCapSuaChua.value?.id ?? 0,
          khoId: selectingKho.value?.id ?? 0,
          from: form.value.from,
          to: form.value.to,
          toaXeId: selectingToaXe.value?.id ?? 0,
        });

        items.value = response;

        isFiltersChanging.value = false;

        await endLoading();
      } catch (e) {
        await endLoading();

        await swalErrNotification(e, 'Có lỗi xảy ra');
      }
    } else return false;
  });
}

return (_ctx: any,_cache: any) => {
  const _component_el_option = _resolveComponent("el-option")!
  const _component_el_select = _resolveComponent("el-select")!
  const _component_el_form_item = _resolveComponent("el-form-item")!
  const _component_el_date_picker = _resolveComponent("el-date-picker")!
  const _component_el_form = _resolveComponent("el-form")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(KeHoachChiPhiPrintModal, {
      ref_key: "printModalRef",
      ref: printModalRef,
      "cap-sua-chua": selectingCapSuaChua.value,
      from: new Date(form.value.from) ?? null,
      items: items.value,
      kho: selectingKho.value ?? null,
      to: new Date(form.value.to) ?? null,
      "toa-xe": selectingToaXe.value,
      onClose: _cache[0] || (_cache[0] = ($event: any) => (printModalRef.value.isOpen = false))
    }, null, 8, ["cap-sua-chua", "from", "items", "kho", "to", "toa-xe"]),
    _createVNode(PageRowLayout, null, {
      "page-row-header": _withCtx(() => [
        _createTextVNode(" Bảng kê chi tiết xuất theo đối tượng ")
      ]),
      "page-row-content": _withCtx(() => [
        _createVNode(_component_el_form, {
          ref_key: "formRef",
          ref: formRef,
          model: form.value,
          rules: rules.value
        }, {
          default: _withCtx(() => [
            _createVNode(ResponsiveLayout, null, {
              "col-1": _withCtx(() => [
                _createVNode(LabelCustom, {
                  "is-text-muted": true,
                  "is-required": true
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(" Kho: ")
                  ]),
                  _: 1
                })
              ]),
              "col-2": _withCtx(() => [
                _createVNode(_component_el_form_item, {
                  prop: "khoId",
                  class: "mb-0"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_select, {
                      modelValue: form.value.khoId,
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form.value.khoId) = $event)),
                      class: "w-100",
                      name: "khoId"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_option, {
                          label: "Chưa chọn",
                          value: -1
                        }),
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(khoItems.value, (kho) => {
                          return (_openBlock(), _createBlock(_component_el_option, {
                            key: kho.id,
                            label: kho.ten_kho,
                            value: kho.id
                          }, null, 8, ["label", "value"]))
                        }), 128))
                      ]),
                      _: 1
                    }, 8, ["modelValue"])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(ResponsiveLayout, null, {
              "col-1": _withCtx(() => [
                _createVNode(LabelCustom, {
                  "is-text-muted": true,
                  "is-required": true
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(" Ngày vào xưởng: ")
                  ]),
                  _: 1
                })
              ]),
              "col-2": _withCtx(() => [
                _createVNode(_component_el_form_item, {
                  prop: "from",
                  class: "mb-0"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_date_picker, {
                      modelValue: form.value.from,
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((form.value.from) = $event)),
                      class: "w-100",
                      "disabled-date": (time) => _unref(disableDate)(time, currentYear.value, true),
                      type: "date",
                      placeholder: "Từ",
                      name: "from",
                      format: "DD/MM/YYYY"
                    }, null, 8, ["modelValue", "disabled-date"])
                  ]),
                  _: 1
                })
              ]),
              "col-3": _withCtx(() => [
                _createVNode(LabelCustom, {
                  "is-text-muted": true,
                  "is-required": true
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(" Ngày xuất xưởng: ")
                  ]),
                  _: 1
                })
              ]),
              "col-4": _withCtx(() => [
                _createVNode(_component_el_form_item, {
                  prop: "to",
                  class: "mb-0"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_date_picker, {
                      modelValue: form.value.to,
                      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((form.value.to) = $event)),
                      class: "w-100",
                      "disabled-date": (time) => _unref(disableDate)(time, currentYear.value, true),
                      type: "date",
                      placeholder: "đến",
                      name: "to",
                      format: "DD/MM/YYYY"
                    }, null, 8, ["modelValue", "disabled-date"])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(ResponsiveLayout, { class: "mb-5" }, {
              "col-1": _withCtx(() => [
                _createVNode(LabelCustom, {
                  "is-text-muted": true,
                  "is-required": true
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(" Toa xe: ")
                  ]),
                  _: 1
                })
              ]),
              "col-2": _withCtx(() => [
                _createVNode(_component_el_form_item, {
                  class: "mb-0",
                  prop: "toaXeId"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_select, {
                      modelValue: form.value.toaXeId,
                      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((form.value.toaXeId) = $event)),
                      class: "w-100",
                      filterable: "",
                      remote: "",
                      placeholder: "mã hoặc tên vật tư",
                      "remote-method": searchToaXe,
                      loading: searchingToaXe.value,
                      "loading-text": "đang tìm kiếm ...",
                      name: "toaXeId"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_option, {
                          label: "Chưa chọn",
                          value: -1
                        }),
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(toaXeOptions.value, (toaXe) => {
                          return (_openBlock(), _createBlock(_component_el_option, {
                            key: toaXe.id,
                            label: toaXe.ma_toa_xe + ' - ' + toaXe.ten_toa_xe,
                            value: toaXe.id
                          }, {
                            default: _withCtx(() => [
                              _createElementVNode("span", _hoisted_1, _toDisplayString(toaXe.ma_toa_xe) + " - " + _toDisplayString(toaXe.ten_toa_xe), 1)
                            ]),
                            _: 2
                          }, 1032, ["label", "value"]))
                        }), 128))
                      ]),
                      _: 1
                    }, 8, ["modelValue", "loading"])
                  ]),
                  _: 1
                })
              ]),
              "col-3": _withCtx(() => [
                _createVNode(LabelCustom, {
                  "is-text-muted": true,
                  "is-required": true
                }, {
                  default: _withCtx(() => [
                    _createTextVNode("Cấp S/C:")
                  ]),
                  _: 1
                })
              ]),
              "col-4": _withCtx(() => [
                _createVNode(_component_el_form_item, {
                  prop: "capSuaChuaId",
                  class: "mb-0"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_select, {
                      modelValue: form.value.capSuaChuaId,
                      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((form.value.capSuaChuaId) = $event)),
                      class: "w-100",
                      name: "capSuaChuaId"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_option, {
                          label: "Chưa chọn",
                          value: -1
                        }),
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(capSuaChuaItems.value, (item) => {
                          return (_openBlock(), _createBlock(_component_el_option, {
                            key: item.id,
                            label: item.ten_cap_sc,
                            value: item.id
                          }, null, 8, ["label", "value"]))
                        }), 128))
                      ]),
                      _: 1
                    }, 8, ["modelValue"])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(DivRow, null, {
              default: _withCtx(() => [
                _createVNode(DivCol, { cols: 8 }),
                _createVNode(DivCol, {
                  cols: 4,
                  class: "d-flex justify-content-end"
                }, {
                  default: _withCtx(() => [
                    (showPrintButton.value)
                      ? (_openBlock(), _createBlock(ButtonCustom, {
                          key: 0,
                          "button-type": _unref(ButtonsType).FA_PRINT,
                          onClick: _cache[6] || (_cache[6] = ($event: any) => (printModalRef.value.isOpen = true))
                        }, {
                          "button-label": _withCtx(() => [
                            _createTextVNode("In phiếu")
                          ]),
                          _: 1
                        }, 8, ["button-type"]))
                      : _createCommentVNode("", true),
                    _createVNode(ButtonCustom, {
                      "button-type": _unref(ButtonsType).FA_FILE_EXPORT_THIN,
                      "color-type": _unref(ButtonTypeColors).SUCCESS,
                      onClick: generate
                    }, null, 8, ["button-type", "color-type"])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["model", "rules"]),
        _createElementVNode("div", _hoisted_2, [
          _createVNode(ChiTietXuatTheoDoiTuongTable, { items: items.value }, null, 8, ["items"])
        ])
      ]),
      _: 1
    })
  ], 64))
}
}

})